import React, { FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Avatar from 'components/UI/Avatar';
import LoaderContent from 'components/UI/LoaderContent';
import { ReactComponent as ListIcon } from 'assets/icons/List.svg';
import { ReactComponent as BookmarkIcon } from 'assets/icons/Bookmark.svg';
import { ReactComponent as ChevronLeft } from 'assets/icons/ChevronLeft.svg';

import { ROUTE_ADD_TO_LIST } from 'routes';
import { useSavePickMutation } from 'constants/graphqlTypes';
import useToast from 'helpers/useToast';
import { useFinishAddContent } from '../helpers/hooks';
import { INiceFindLocationStateVariable } from './models';

import {
  StyledBackground,
  StyledContent,
  StyledViewControls,
  StyledBackButton,
  StyledMessageContainer,
  StyledTitle,
  StyledDomainName,
  StyledActionSheet,
  StyledActionItem,
  StyledAvatarCircle,
  StyledActionIcon,
  StyledActionText,
  StyledActionTitle,
  StyledActionSubtitle,
  StyledDivider,
  StyledSavedTitleText,
  StyledSaveIcon,
  StyledSubTitle,
} from './styled';

interface INiceFindProps {
  handleClose: () => void;
}

const NiceFind: FC<INiceFindProps> = ({ handleClose }) => {
  const [isSaved, setIsSaved] = useState(false);
  const { state, pathname } = useLocation<{ variable: INiceFindLocationStateVariable }>();

  const { variable } = state ?? {};
  const { sourceUrl, sourceDomainName, userData, cardImage, pickId, cardId } = variable ?? {};

  const [savePick, { loading }] = useSavePickMutation({ onCompleted: () => handleClose() });
  const { setToast } = useToast();

  const handleSavePick = () =>
    savePick({
      variables: { url: sourceUrl ?? '', referrerPickId: variable?.referrerPickId || undefined } as any,
      onError: ({ message }) => setToast({ isToastOpen: true, toastItemName: message, toastError: true }),
    });

  const { replace, goBack } = useHistory();

  const { handleFinish: handleAddToProfile, loading: addToProfileLoading } = useFinishAddContent({
    pickId: pickId ?? '',
    cardId: cardId ?? '',
    thought: '',
    isUpdateThought: false,
    listTitle: '',
    close: handleClose,
  });

  const goToNextStep = (targetPathname: string) =>
    replace({
      pathname: targetPathname,
      state: { ...state, variable: { ...state.variable, prevPath: pathname, isFromNiceFind: true } },
    });

  return (
    <>
      <StyledBackground backgroundImage={cardImage} />
      <StyledContent>
        <StyledViewControls>
          <StyledBackButton onClick={() => goBack()} aria-label="Go back">
            <ChevronLeft />
          </StyledBackButton>
          <div />
        </StyledViewControls>

        {(loading || addToProfileLoading) && <LoaderContent isCentered isFullScreen isDark />}

        {!loading && !addToProfileLoading && isSaved && (
          <StyledMessageContainer>
            <StyledSaveIcon>
              <BookmarkIcon width={48} height={48} viewBox="0 0 24 24" />
            </StyledSaveIcon>
            <StyledSavedTitleText>Saved for later.</StyledSavedTitleText>
            <StyledSubTitle>{sourceDomainName}</StyledSubTitle>
          </StyledMessageContainer>
        )}

        {!loading && !addToProfileLoading && !isSaved && (
          <>
            <StyledMessageContainer>
              <StyledTitle>Nice find.</StyledTitle>
              <StyledDomainName>{sourceDomainName}</StyledDomainName>
            </StyledMessageContainer>

            <StyledActionSheet>
              <StyledActionItem onClick={handleAddToProfile}>
                <StyledAvatarCircle>{userData && <Avatar user={userData} dimension={52} />}</StyledAvatarCircle>
                <StyledActionText>
                  <StyledActionTitle>Add to profile</StyledActionTitle>
                  <StyledActionSubtitle>Quick sharing</StyledActionSubtitle>
                </StyledActionText>
              </StyledActionItem>
              <StyledDivider />
              <StyledActionItem onClick={() => goToNextStep(ROUTE_ADD_TO_LIST)}>
                <StyledActionIcon>
                  <ListIcon />
                </StyledActionIcon>
                <StyledActionText>
                  <StyledActionTitle>Add to list · roundup · spark</StyledActionTitle>
                  <StyledActionSubtitle>Add to profile in an organized way</StyledActionSubtitle>
                </StyledActionText>
              </StyledActionItem>
              <StyledDivider />
              <StyledActionItem onClick={handleSavePick}>
                <StyledActionIcon>
                  <BookmarkIcon />
                </StyledActionIcon>
                <StyledActionText>
                  <StyledActionTitle>Save for later</StyledActionTitle>
                  <StyledActionSubtitle>Private discovery queue</StyledActionSubtitle>
                </StyledActionText>
              </StyledActionItem>
            </StyledActionSheet>
          </>
        )}
      </StyledContent>
    </>
  );
};

export default NiceFind;
